import type {
  AccountOption,
  MeAuthorization,
  MeBindingItem,
  MeLoginLog,
  MeSession,
  MenuNode,
  PageResult,
  PermissionSnapshot,
  QueryParams,
  SecurityOverview,
  SystemOption,
  UserProfile,
  WechatBind,
} from '@/types/api'
import { del, get, patch, post, put } from '@/utils/request'

/**
 * 个人中心 / 管理后台的自助接口（附录B §4、附录C §4）。
 *
 * 菜单与权限都按「系统」下发：`systemUuid` 必填，`serviceCode` 可选（SDK / 业务服务按服务拉取）。
 * 账号（人 × 公司）决定数据隔离边界，系统决定功能边界。
 */
export const meApi = {
  profile: () => get<UserProfile>('/me/v1/me'),
  updateProfile: (payload: Partial<UserProfile>) => patch<UserProfile>('/me/v1/me', payload),

  /** 账号切换器：当前用户可进入的后台账号（一家公司一个）。 */
  accounts: () => get<AccountOption[]>('/me/v1/me/accounts'),
  /** 系统切换器：缺省取令牌里的公司；业务用户返回其绑定过的系统。 */
  systems: (companyUuid?: string) => get<SystemOption[]>('/me/v1/me/systems', { companyUuid }),

  /** 侧边菜单唯一数据源：换系统必须重新拉取；scope 控制管理/系统服务切片。 */
  menus: (params: { systemUuid: string; serviceCode?: string; scope?: string }) =>
    get<MenuNode[]>('/me/v1/me/menus', {
      systemUuid: params.systemUuid,
      serviceCode: params.serviceCode,
      scope: params.scope,
    }),
  permissions: (params: { systemUuid: string; serviceCode?: string; scope?: string }) =>
    get<PermissionSnapshot>('/me/v1/me/permissions', {
      systemUuid: params.systemUuid,
      serviceCode: params.serviceCode,
      scope: params.scope,
    }),

  // 安全设置
  security: () => get<SecurityOverview>('/me/v1/me/security'),
  changePassword: (payload: { oldPassword: string; newPassword: string }) =>
    post<void>('/me/v1/me/password', payload),
  bindMobile: (payload: { mobile: string; code: string }) => post<void>('/me/v1/me/mobile', payload),
  bindEmail: (payload: { email: string; code: string }) => post<void>('/me/v1/me/email', payload),
  setMfa: (enabled: boolean) => post<void>('/me/v1/me/mfa', { enabled }),

  // 我的绑定
  bindings: () => get<MeBindingItem[]>('/me/v1/me/bindings'),
  unbind: (id: string) => del<void>(`/me/v1/me/bindings/${id}`),
  setPrimary: (id: string) => put<void>(`/me/v1/me/bindings/${id}/primary`),

  // 已授权系统
  authorizations: () => get<MeAuthorization[]>('/me/v1/me/authorizations'),
  revokeAuthorization: (id: string) => del<void>(`/me/v1/me/authorizations/${id}`),

  // 登录设备
  sessions: () => get<MeSession[]>('/me/v1/me/sessions'),
  kickSession: (id: string) => del<void>(`/me/v1/me/sessions/${id}`),

  // 登录记录
  loginLogs: (params?: QueryParams) => get<PageResult<MeLoginLog>>('/me/v1/me/login/logs', params),

  // 微信绑定
  wechat: () => get<WechatBind[]>('/me/v1/me/wechat'),
  unbindWechat: (id: string) => del<void>(`/me/v1/me/wechat/${id}`),

  // 隐私与数据
  exportData: () => post<{ taskId: string }>('/me/v1/me/data/export'),
  closeAccount: (payload: { reason?: string; verifyCode: string }) =>
    post<void>('/me/v1/me/account/close', payload),
}
